import { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import PTOCalendar from '../components/PTOCalendar';
import PlaylistWall from '../components/PlaylistWall';
import SnakeGame from '../components/games/SnakeGame';
import FlappyGame from '../components/games/FlappyGame';
import PinballGame from '../components/games/PinballGame';
import SelfDestruct, { DoNotPressButton } from '../components/SelfDestruct';

const QUOTES = [
  "“I'm not lazy, I'm on energy-saving mode.”",
  "“Coffee: because adulting is hard.”",
  "“My favorite exercise is a cross between a lunge and a crunch. I call it lunch.”",
  "“Teamwork is important; it helps to put the blame on someone else.”",
  "“I love deadlines. I like the whooshing sound they make as they fly by.”",
  "“Work smarter, not harder. Then nap.”",
  "“The closest I get to a salad is a Caesar dressing chip.”",
  "“Monday is just a reminder that the weekend was too short.”",
  "“I put the ‘pro’ in procrastinate.”",
  "“Out of office. Probably forever.”",
];

const PIN_COLORS = ['#d32f2f', '#2563eb', '#2e9e4f', '#e0a800', '#8e44ad'];
const NOTE_COLORS = { yellow: '#fff7a8', pink: '#ffc2d4', blue: '#b8e2ff', green: '#c7f5c0', orange: '#ffd9a8' };
const HAND = "'Comic Sans MS', 'Marker Felt', 'Segoe Print', cursive";

// Resize/compress an image file to a small JPEG data URL before upload
function resizeImage(file, maxDim = 1000, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim; }
        else if (height >= width && height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim; }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const GAMES = [
  { k: 'snake', name: 'Snake', emoji: '🐍', org: 'Reptile Relocation Services', tag: '“We grow on you.”', pin: '#2e9e4f' },
  { k: 'flappy', name: 'Flappy', emoji: '🐤', org: 'Avian Aviation Co.', tag: '“No refunds. Ever.”', pin: '#e0a800' },
  { k: 'pinball', name: 'Pinball', emoji: '☎️', org: '811 — Call Before You Dig', tag: '“Know what\'s below. Flip before you dig.”', pin: '#e07b00' },
];

function Pin({ color = '#d32f2f' }) {
  return (
    <span
      className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full z-10"
      style={{
        width: 18, height: 18,
        background: `radial-gradient(circle at 35% 30%, #ffffffcc, ${color} 55%, #00000055)`,
        boxShadow: '0 3px 4px rgba(0,0,0,0.45)',
      }}
    />
  );
}

// Hand-drawn marker tally marks
function Tally({ count }) {
  const groups = [];
  let r = count;
  while (r > 0) { groups.push(Math.min(5, r)); r -= 5; }
  const W = Math.max(groups.length, 1) * 44 + 12;
  const stroke = '#3a2a14';
  return (
    <svg width={W} height={46} style={{ overflow: 'visible' }}>
      {groups.map((g, gi) => {
        const x = gi * 44 + 8;
        const parts = [];
        const verts = Math.min(g, 4);
        for (let i = 0; i < verts; i++) {
          const lx = x + i * 8;
          parts.push(<line key={`v${gi}-${i}`} x1={lx} y1={8} x2={lx + (i % 2 ? 1.5 : -1)} y2={38} stroke={stroke} strokeWidth={3.5} strokeLinecap="round" />);
        }
        if (g === 5) parts.push(<line key={`d${gi}`} x1={x - 5} y1={37} x2={x + 27} y2={9} stroke={stroke} strokeWidth={3.5} strokeLinecap="round" />);
        return <g key={gi}>{parts}</g>;
      })}
      {count === 0 && <text x={4} y={28} fill="#5b3a1a" fontSize="13" fontStyle="italic">none yet…</text>}
    </svg>
  );
}

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const diff = new Date(target).getTime() - now;
  const days = Math.ceil(diff / 86400000);
  if (days <= 0) return <p className="text-center font-black text-lg" style={{ color: '#2e7d32' }}>IT'S TODAY! 🎉</p>;
  return (
    <div className="text-center leading-none">
      <div className="text-6xl font-black" style={{ color: '#7a4f25' }}>{days}</div>
      <div className="text-[11px] font-bold uppercase tracking-widest mt-1" style={{ color: '#9a7a4a' }}>day{days === 1 ? '' : 's'} until</div>
    </div>
  );
}

function Confetti() {
  const pieces = Array.from({ length: 40 });
  const emojis = ['🎉', '🎊', '⭐', '🏆', '✨', '🥳'];
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {pieces.map((_, i) => (
        <span key={i} className="absolute text-2xl" style={{ left: `${Math.random() * 100}%`, top: '-40px', animation: `br-fall ${2 + Math.random() * 2}s linear ${Math.random()}s forwards` }}>
          {emojis[i % emojis.length]}
        </span>
      ))}
      <style>{`@keyframes br-fall { to { transform: translateY(110vh) rotate(720deg); opacity: 0.2; } }`}</style>
    </div>
  );
}

function Leaderboard({ game, refreshKey }) {
  const [scores, setScores] = useState([]);
  useEffect(() => {
    api.get(`/breakroom/scores/${game}`).then(({ data }) => setScores(data)).catch(() => {});
  }, [game, refreshKey]);
  const medals = ['🥇', '🥈', '🥉'];
  return (
    <div>
      <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">🏆 Hall of Fame</h4>
      {scores.length === 0 ? (
        <p className="text-gray-500 text-xs italic">No legends yet. Go make history.</p>
      ) : (
        <ol className="space-y-1">
          {scores.map((s, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className="text-gray-300"><span className="mr-2">{medals[i] || `#${i + 1}`}</span>{s.player_name}</span>
              <span className="font-black text-gold-500 tabular-nums">{s.score}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function StickyNote({ n, canDelete, onDelete }) {
  const rot = ((n.id * 37) % 7) - 3;
  // A note with a photo is a polaroid pinned to the board
  if (n.photo) {
    return (
      <div className="relative pt-2 px-2 pb-7" style={{ width: 176, backgroundColor: '#fdfdfb', transform: `rotate(${rot}deg)`, boxShadow: '0 6px 14px rgba(0,0,0,0.38)' }}>
        <Pin color="#455a64" />
        {canDelete && <button onClick={onDelete} className="absolute top-1 right-2 text-gray-400 hover:text-red-600 text-xs z-10">✕</button>}
        <img src={n.photo} alt={n.body || 'photo'} className="w-full object-cover block" style={{ height: 150, backgroundColor: '#111' }} />
        {n.body && <p className="text-center text-sm mt-2 px-1 break-words leading-tight" style={{ color: '#2b2b2b', fontFamily: HAND }}>{n.body}</p>}
        <p className="absolute bottom-1.5 right-3 text-[10px] italic" style={{ color: '#999' }}>— {n.author_name}</p>
      </div>
    );
  }
  const bg = NOTE_COLORS[n.color] || NOTE_COLORS.yellow;
  return (
    <div className="relative rounded-sm p-3 pb-6" style={{ width: 168, minHeight: 150, backgroundColor: bg, transform: `rotate(${rot}deg)`, boxShadow: '0 5px 10px rgba(0,0,0,0.3)' }}>
      <Pin color="#c0392b" />
      {canDelete && <button onClick={onDelete} className="absolute top-1 right-2 text-gray-500 hover:text-red-600 text-xs">✕</button>}
      <p className="text-sm whitespace-pre-wrap break-words pt-1" style={{ color: '#2b2b2b', fontFamily: HAND }}>{n.body}</p>
      <p className="absolute bottom-1.5 right-3 text-[10px] italic" style={{ color: '#555' }}>— {n.author_name}</p>
    </div>
  );
}

function Composer({ onPost, onClose }) {
  const [body, setBody] = useState('');
  const [color, setColor] = useState('yellow');
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  async function pickPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try { setPhoto(await resizeImage(file)); } catch { alert('Could not read that image.'); }
    setBusy(false);
  }

  const canPost = (body.trim() || photo) && !busy;

  return (
    <div className="mb-4 rounded-lg p-4 br-dark">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={280}
        rows={photo ? 2 : 3}
        placeholder={photo ? 'Add a caption… (optional)' : "What's on your mind? (gossip, snacks, motivational threats…)"}
        className="w-full rounded px-3 py-2 text-sm text-white border focus:outline-none resize-none"
        style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', fontFamily: HAND }}
      />

      {photo && (
        <div className="mt-2 inline-block relative">
          <img src={photo} alt="preview" className="rounded" style={{ maxHeight: 120, border: '3px solid #fdfdfb' }} />
          <button onClick={() => setPhoto(null)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs leading-none" title="Remove photo">✕</button>
        </div>
      )}

      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {!photo && Object.entries(NOTE_COLORS).map(([k, c]) => (
            <button key={k} type="button" onClick={() => setColor(k)} className="rounded-full" style={{ width: 22, height: 22, backgroundColor: c, border: color === k ? '2px solid #c9a84c' : '2px solid #00000022' }} />
          ))}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="text-xs text-gray-300 hover:text-white border border-navy-500 rounded px-2 py-1 disabled:opacity-50">
            {busy ? 'Loading…' : photo ? '📷 Change photo' : '📷 Photo'}
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="text-xs text-gray-400 hover:text-white uppercase tracking-wide px-2">Cancel</button>
          <button
            onClick={() => { if (canPost) onPost(body, color, photo); }}
            disabled={!canPost}
            className="px-4 py-1.5 rounded text-sm font-bold uppercase tracking-wide disabled:opacity-50"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >
            📌 Stick it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BreakRoom() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [playGame, setPlayGame] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confetti, setConfetti] = useState(false);
  const [quote] = useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [coffeeBoard, setCoffeeBoard] = useState([]); // [{ name, count }] for today, shared
  const [destruct, setDestruct] = useState(false);
  const [celebrations, setCelebrations] = useState([]);

  useEffect(() => { fetchEvents(); fetchNotes(); fetchCoffee(); fetchCelebrations(); }, []);

  // Keep the shared coffee board fresh while the page is open
  useEffect(() => {
    const t = setInterval(fetchCoffee, 30000);
    return () => clearInterval(t);
  }, []);

  async function fetchCoffee() {
    try { const { data } = await api.get('/breakroom/coffee'); setCoffeeBoard(data); } catch {}
  }

  async function fetchEvents() {
    try { const { data } = await api.get('/breakroom/events'); setEvents(data); } catch {}
  }
  async function fetchNotes() {
    try { const { data } = await api.get('/breakroom/notes'); setNotes(data); } catch {}
  }
  async function fetchCelebrations() {
    try { const { data } = await api.get('/breakroom/celebrations'); setCelebrations(data); } catch {}
  }

  // Today's celebrations (match by month/day) for the banner
  const now = new Date();
  const todayCelebs = celebrations.filter((c) => c.event_month === now.getMonth() + 1 && c.event_day === now.getDate());
  const birthdaysToday = todayCelebs.filter((c) => c.type === 'birthday');
  const annivsToday = todayCelebs.filter((c) => c.type === 'anniversary');

  const handleScore = useCallback(async (score) => {
    if (!playGame) return;
    try {
      const { data } = await api.post('/breakroom/scores', { game: playGame, score });
      setRefreshKey((k) => k + 1);
      if (data.isHighScore && score > 0) {
        setConfetti(true);
        setTimeout(() => setConfetti(false), 4000);
      }
    } catch {}
  }, [playGame]);


  async function postNote(body, color, photo) {
    try {
      const { data } = await api.post('/breakroom/notes', { body, color, photo });
      setNotes((prev) => [data, ...prev]);
      setComposerOpen(false);
    } catch { alert('Failed to post note'); }
  }
  async function deleteNote(id) {
    try { await api.delete(`/breakroom/notes/${id}`); setNotes((prev) => prev.filter((n) => n.id !== id)); } catch {}
  }

  async function sipCoffee() {
    try {
      const { data } = await api.post('/breakroom/coffee');
      setCoffeeBoard(data);
    } catch {}
  }

  return (
    <Layout>
      {confetti && <Confetti />}

      {/* Game modal */}
      {playGame && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.78)' }}>
          <div className="br-dark rounded-xl p-5 w-full max-w-md relative">
            <button onClick={() => setPlayGame(null)} className="absolute top-3 right-4 text-gray-400 hover:text-white text-2xl leading-none">×</button>
            {playGame === 'snake' ? <SnakeGame onScore={handleScore} /> : playGame === 'flappy' ? <FlappyGame onScore={handleScore} /> : <PinballGame onScore={handleScore} />}
            <div className="mt-5 pt-4 border-t" style={{ borderColor: '#1e3a5f' }}>
              <Leaderboard game={playGame} refreshKey={refreshKey} />
            </div>
          </div>
        </div>
      )}

      {destruct && <SelfDestruct />}

      <style>{`
        .br-cork {
          background-color: #c8a06a;
          background-image:
            radial-gradient(circle at 20% 35%, rgba(120,72,40,0.28) 0 1.5px, transparent 2px),
            radial-gradient(circle at 65% 15%, rgba(90,54,30,0.22) 0 1.5px, transparent 2px),
            radial-gradient(circle at 80% 60%, rgba(150,100,55,0.30) 0 1.5px, transparent 2px),
            radial-gradient(circle at 40% 80%, rgba(110,68,38,0.24) 0 1.5px, transparent 2px),
            radial-gradient(circle at 50% 50%, rgba(255,235,200,0.18) 0 1px, transparent 1.5px);
          background-size: 23px 23px, 29px 29px, 31px 31px, 37px 37px, 17px 17px;
        }
        .br-paper { background:#fdfaf2; box-shadow:0 5px 14px rgba(0,0,0,0.35); }
        .br-sticky { background:#fff7a8; box-shadow:0 5px 10px rgba(0,0,0,0.3); }
        .br-dark { background:#0d1b2e; box-shadow:0 6px 16px rgba(0,0,0,0.45); }
      `}</style>

      <div className="max-w-6xl mx-auto">
        <div style={{ padding: 14, borderRadius: 14, background: 'linear-gradient(135deg,#6b4423,#8a5a2b,#5b3a1a)', boxShadow: '0 12px 40px rgba(0,0,0,0.55)' }}>
          <div className="br-cork space-y-8" style={{ borderRadius: 6, padding: '28px 24px', boxShadow: 'inset 0 0 40px rgba(80,48,24,0.45)' }}>

            {/* Celebration banners */}
            {birthdaysToday.length > 0 && (
              <div className="rounded-lg px-5 py-3 text-center shadow-lg" style={{ background: 'linear-gradient(90deg,#ff6fae,#ffb86c,#ff6fae)', color: '#3a0a25' }}>
                <p className="font-black text-lg">🎂🎉 Happy Birthday, {birthdaysToday.map((c) => c.person_name).join(' & ')}! 🎉🎂</p>
              </div>
            )}
            {annivsToday.length > 0 && (
              <div className="rounded-lg px-5 py-3 text-center shadow-lg" style={{ background: 'linear-gradient(90deg,#c9a84c,#f5e9d0,#c9a84c)', color: '#3f2912' }}>
                <p className="font-black text-lg">
                  🏆 {annivsToday.map((c) => {
                    const yrs = c.start_year ? new Date().getFullYear() - c.start_year : null;
                    return `${c.person_name}${yrs ? ` — ${yrs} year${yrs === 1 ? '' : 's'}` : ''}`;
                  }).join(' · ')} — Happy Work Anniversary! 🏆
                </p>
              </div>
            )}

            {/* Header — wooden sign */}
            <div className="text-center">
              <div className="inline-block px-8 py-3 rounded-lg" style={{ background: 'linear-gradient(#7a4f25,#5b3a1a)', border: '2px solid #3f2912', boxShadow: '0 6px 14px rgba(0,0,0,0.4)' }}>
                <h1 className="text-3xl font-black tracking-widest uppercase" style={{ color: '#f5e9d0', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>🎮 The Break Room</h1>
              </div>
              <p className="text-sm mt-3 italic font-semibold" style={{ color: '#3f2912' }}>{quote}</p>
            </div>

            {/* Shared coffee tally (everyone's cups, marker on cork) + the button */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-[240px]">
                <div className="flex items-center gap-3 mb-1.5">
                  <span className="text-sm font-bold" style={{ color: '#3a2a14', fontFamily: HAND, transform: 'rotate(-2deg)', display: 'inline-block' }}>☕ Coffees Today</span>
                  <button onClick={sipCoffee} className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: '#fdfaf2', color: '#7a4f25', boxShadow: '0 2px 4px rgba(0,0,0,0.25)' }}>+ add yours</button>
                </div>
                {coffeeBoard.length === 0 ? (
                  <p className="text-xs italic" style={{ color: '#5b3a1a' }}>Nobody's caffeinated yet… be the hero ☕</p>
                ) : (
                  <div className="space-y-0.5">
                    {coffeeBoard.map((c) => (
                      <div key={c.name} className="flex items-center gap-3">
                        <span className="text-xs font-bold w-20 truncate" style={{ color: '#3a2a14', fontFamily: HAND }}>{c.name.split(' ')[0]}</span>
                        <Tally count={c.count} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DoNotPressButton onPress={() => setDestruct(true)} />
            </div>

            {/* Post-it Wall */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-black uppercase tracking-widest" style={{ color: '#3f2912' }}>📌 The Wall</h2>
                {!composerOpen && (
                  <button onClick={() => setComposerOpen(true)} className="px-4 py-1.5 rounded text-sm font-bold uppercase tracking-wide" style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>
                    ✍️ Write a Post-it
                  </button>
                )}
              </div>
              {composerOpen && <Composer onPost={postNote} onClose={() => setComposerOpen(false)} />}
              {notes.length === 0 ? (
                <p className="text-sm italic" style={{ color: '#5b3a1a' }}>Bare board. Stick the first note and start the chaos.</p>
              ) : (
                <div className="flex flex-wrap gap-5">
                  {notes.map((n) => (
                    <StickyNote key={n.id} n={n} canDelete={n.author_id === user?.id || isAdmin} onDelete={() => deleteNote(n.id)} />
                  ))}
                </div>
              )}
            </section>

            {/* Countdown — days-until post-its */}
            <section>
              <h2 className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: '#3f2912' }}>⏳ The Countdown to Freedom</h2>
              {events.length === 0 ? (
                <p className="text-sm italic" style={{ color: '#5b3a1a' }}>No events on the horizon. Bleak.</p>
              ) : (
                <div className="flex flex-wrap gap-5">
                  {events.map((ev, i) => (
                    <div key={ev.id} className="br-sticky rounded relative p-4" style={{ width: 180, transform: `rotate(${(i % 3 - 1) * 1.5}deg)` }}>
                      <Pin color={PIN_COLORS[i % PIN_COLORS.length]} />
                      <p className="text-center text-3xl mb-1">{ev.emoji}</p>
                      <Countdown target={ev.event_date} />
                      <p className="text-center font-bold text-sm mt-2" style={{ color: '#5b3a1a', fontFamily: HAND }}>{ev.title}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Arcade business cards + Calendar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <section>
                <h2 className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: '#3f2912' }}>🕹️ Arcade — take a card, play a round</h2>
                <div className="flex flex-wrap gap-5">
                  {GAMES.map((g, i) => (
                    <button
                      key={g.k}
                      onClick={() => setPlayGame(g.k)}
                      className="br-paper relative rounded text-left transition hover:-translate-y-0.5"
                      style={{ width: 232, padding: '16px 18px', transform: `rotate(${(i % 2 ? 1 : -1.2)}deg)` }}
                    >
                      <Pin color={g.pin} />
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{g.emoji}</span>
                        <div>
                          <p className="font-black text-base tracking-wide" style={{ color: '#222' }}>{g.name.toUpperCase()}</p>
                          <p className="text-[10px] uppercase tracking-wide" style={{ color: '#7a6a4a' }}>{g.org}</p>
                        </div>
                      </div>
                      <p className="text-[12px] italic mt-3" style={{ color: '#555' }}>{g.tag}</p>
                      <p className="text-[11px] mt-3 font-black tracking-wide" style={{ color: '#b3322a' }}>▶ TAP TO PLAY</p>
                    </button>
                  ))}
                </div>
              </section>

              {/* PTO Calendar — paper, pinned */}
              <section className="br-paper rounded relative p-3" style={{ transform: 'rotate(1deg)' }}>
                <Pin color="#2563eb" />
                <PTOCalendar celebrations={celebrations} />
              </section>
            </div>

            {/* Staff Picks — the record sleeve at the bottom of the board */}
            <div className="flex justify-center">
              <section className="br-paper relative rounded" style={{ width: '100%', maxWidth: 860, transform: 'rotate(-0.5deg)' }}>
                <Pin color="#1db954" />
                <div style={{ margin: 10, border: '2px solid #2b2b2b', padding: '18px 20px 20px' }}>
                  <div className="text-center pb-3 mb-4" style={{ borderBottom: '3px double #2b2b2b' }}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.3em]" style={{ color: '#1db954' }}>★ Now Playing ★</p>
                    <h2 className="text-3xl font-black uppercase tracking-wider mt-1" style={{ color: '#1a1a1a', fontFamily: 'Georgia, serif' }}>♫ Staff Picks ♫</h2>
                    <p className="text-sm italic mt-1" style={{ color: '#444' }}>Pin a playlist. Judge everyone's taste. Quietly.</p>
                  </div>
                  <PlaylistWall />
                </div>
              </section>
            </div>

            <p className="text-center text-xs pb-1 italic font-semibold" style={{ color: '#3f2912' }}>
              What happens in the break room stays in the break room. Except high scores. Those are forever.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
